import React, { useEffect, useState } from "react";
import {
  Card,
  Switch,
  Button,
  Space,
  Spin,
  message,
  Form,
  DatePicker,
  Divider,
  Alert,
  Radio,
  Row,
  Col,
  Tag,
  Tabs,
  Tooltip,
} from "antd";
import {
  LockOutlined,
  SaveOutlined,
  ReloadOutlined,
  WarningOutlined,
  EyeOutlined,
  SettingOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { transportApi } from "../../api/transportApi";
import { useThemeStore } from "../../stores/themeStore";
import type { NavMode, UiVariant } from "../../stores/themeStore";
import { getTransportPalette, transportAccentOptions } from "../../theme/transportThemes";
import type { TransportAccent } from "../../theme/transportThemes";
import { NAV_SHORTCUTS } from "../../app/layout/ControlRoomLayout";

interface AccountingSettings {
  id: number;
  isAccountingEnabled: boolean;
  autoPostEnabled: boolean;
  isManualBiltyNumberEnabled: boolean;
  isManualManifestNumberEnabled: boolean;
  isManualGatePassNumberEnabled: boolean;
  /** True when BiltyNumbering:IsLocked = true in appsettings.json */
  isNumberingModeLocked?: boolean;
  postingStartDate: string | null;
  lockEntriesBefore: string | null;
  updatedOn: string;
  updatedBy: string;
}

const SettingsPage: React.FC = () => {
  const { uiVariant, setUiVariant, mode, accent, setAccent, navMode, setNavMode } = useThemeStore();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [numberingSaving, setNumberingSaving] = useState(false);
  const [settings, setSettings] = useState<AccountingSettings>({
    id: 0,
    isAccountingEnabled: true,
    autoPostEnabled: true,
    isManualBiltyNumberEnabled: true,   // Manual Bilty is the system default
    isManualManifestNumberEnabled: true,
    isManualGatePassNumberEnabled: true,
    postingStartDate: null,
    lockEntriesBefore: null,
    updatedOn: new Date().toISOString(),
    updatedBy: "",
  });
  const [form] = Form.useForm();

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      isAccountingEnabled: settings.isAccountingEnabled,
      autoPostEnabled: settings.autoPostEnabled,
      isManualBiltyNumberEnabled: settings.isManualBiltyNumberEnabled,
      isManualManifestNumberEnabled: settings.isManualManifestNumberEnabled,
      isManualGatePassNumberEnabled: settings.isManualGatePassNumberEnabled,
      postingStartDate: settings.postingStartDate ? dayjs(settings.postingStartDate) : null,
      lockEntriesBefore: settings.lockEntriesBefore ? dayjs(settings.lockEntriesBefore) : null,
    });
  }, [settings, form]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await transportApi.getAccountingSettings();
      setSettings({
        ...data,
        isManualBiltyNumberEnabled: data.isManualBiltyNumberEnabled !== false,
        isManualManifestNumberEnabled: data.isManualManifestNumberEnabled !== false,
        isManualGatePassNumberEnabled: data.isManualGatePassNumberEnabled !== false,
      });
    } catch (err) {
      message.error("Failed to load settings");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const payload = {
        isAccountingEnabled: values.isAccountingEnabled,
        autoPostEnabled: values.autoPostEnabled,
        isManualBiltyNumberEnabled: values.isManualBiltyNumberEnabled,
        isManualManifestNumberEnabled: values.isManualManifestNumberEnabled,
        isManualGatePassNumberEnabled: values.isManualGatePassNumberEnabled,
        postingStartDate: values.postingStartDate ? values.postingStartDate.toISOString() : null,
        lockEntriesBefore: values.lockEntriesBefore ? values.lockEntriesBefore.toISOString() : null,
      };
      await transportApi.updateAccountingSettings(payload);
      message.success("Settings saved successfully");
      loadSettings();
    } catch (err) {
      message.error("Failed to save settings");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleNumberingModeChange = async (
    field: "isManualBiltyNumberEnabled" | "isManualManifestNumberEnabled" | "isManualGatePassNumberEnabled",
    value: boolean
  ) => {
    form.setFieldValue(field, value);
    setNumberingSaving(true);
    try {
      const next = { ...settings, [field]: value };
      await transportApi.updateDocumentNumberingSettings({
        isManualBiltyNumberEnabled: next.isManualBiltyNumberEnabled,
        isManualManifestNumberEnabled: next.isManualManifestNumberEnabled,
        isManualGatePassNumberEnabled: next.isManualGatePassNumberEnabled,
      });
      setSettings((current) => ({ ...current, [field]: value, updatedOn: new Date().toISOString() }));
      message.success("Document numbering mode saved");
    } catch (err) {
      message.error("Failed to save document numbering mode");
      form.setFieldValue(field, settings[field]);
      console.error(err);
    } finally {
      setNumberingSaving(false);
    }
  };

  const tabItems = [
    {
      key: "accounting",
      label: <Space><SettingOutlined />Accounting</Space>,
      children: (
        <Card bordered={false}>
          <Alert
            message="Feature Toggle"
            description="Enable or disable the Accounting module. When disabled, consignments, trips, and other transport operations will NOT post data to the accounting system."
            type="info"
            style={{ marginBottom: 20 }}
            showIcon
          />
          <Form.Item label="Enable Accounting Module" name="isAccountingEnabled" valuePropName="checked">
            <Switch checkedChildren="ON" unCheckedChildren="OFF" style={{ backgroundColor: "#52c41a" }} />
          </Form.Item>
          <Divider />
          <Form.Item label="Auto-Post Transactions" name="autoPostEnabled" valuePropName="checked">
            <Switch checkedChildren="ON" unCheckedChildren="OFF" disabled={!settings.isAccountingEnabled} />
          </Form.Item>
          <Form.Item label="Posting Start Date" name="postingStartDate">
            <DatePicker disabled={!settings.isAccountingEnabled} placeholder="Select date when posting should begin" />
          </Form.Item>
          <Form.Item label="Lock Entries Before" name="lockEntriesBefore">
            <DatePicker disabled={!settings.isAccountingEnabled} placeholder="Date before which entries cannot be modified" />
          </Form.Item>
          <Divider />
          <Row gutter={16}>
            <Col>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                Save Settings
              </Button>
            </Col>
            <Col>
              <Button icon={<ReloadOutlined />} onClick={loadSettings} disabled={saving}>
                Reset
              </Button>
            </Col>
          </Row>
          {settings.updatedOn && (
            <div style={{ marginTop: 20, fontSize: 12, opacity: 0.7 }}>
              <p>Last updated: {new Date(settings.updatedOn).toLocaleString()}</p>
              {settings.updatedBy && <p>Updated by: {settings.updatedBy}</p>}
            </div>
          )}
        </Card>
      ),
    },
    {
      key: "numbering",
      label: <Space><FileTextOutlined />Document Numbering</Space>,
      children: (
        <Card bordered={false}>
          <Alert
            message="Branch Document Numbering"
            description="Choose manual branch-reserved stationery numbers or automatic document sequences independently for Bilty / LR, Manifest, and Gate Pass."
            type="info"
            style={{ marginBottom: 20 }}
            showIcon
          />

          {settings.isNumberingModeLocked && (
            <Alert
              type="warning"
              showIcon
              icon={<LockOutlined />}
              message="Numbering mode is locked by configuration"
              description={
                <>
                  All document numbering modes are locked by <code>BiltyNumbering:IsLocked = true</code> in <code>appsettings.json</code>.
                  Contact the developer to change them.
                </>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            label={
              <Space>
                Bilty Entry Numbering Mode
                {settings.isNumberingModeLocked && (
                  <Tooltip placement="bottom" title="Locked by appsettings.json — contact the developer to change">
                    <LockOutlined style={{ color: "#faad14" }} />
                  </Tooltip>
                )}
              </Space>
            }
            name="isManualBiltyNumberEnabled"
            rules={[{ required: true, message: "Select numbering mode" }]}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              disabled={numberingSaving || !!settings.isNumberingModeLocked}
              onChange={(event) => {
                if (!settings.isNumberingModeLocked) void handleNumberingModeChange("isManualBiltyNumberEnabled", event.target.value);
              }}
              options={[
                { value: true, label: "Manual branch sequence" },
                { value: false, label: "Automatic document sequence" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Manifest Numbering Mode" name="isManualManifestNumberEnabled" rules={[{ required: true }]}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              disabled={numberingSaving || !!settings.isNumberingModeLocked}
              onChange={(event) => void handleNumberingModeChange("isManualManifestNumberEnabled", event.target.value)}
              options={[
                { value: true, label: "Manual branch sequence" },
                { value: false, label: "Automatic document sequence" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Gate Pass Numbering Mode" name="isManualGatePassNumberEnabled" rules={[{ required: true }]}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              disabled={numberingSaving || !!settings.isNumberingModeLocked}
              onChange={(event) => void handleNumberingModeChange("isManualGatePassNumberEnabled", event.target.value)}
              options={[
                { value: true, label: "Manual branch sequence" },
                { value: false, label: "Automatic document sequence" },
              ]}
            />
          </Form.Item>
        </Card>
      ),
    },
    {
      key: "display",
      label: <Space><EyeOutlined />Display &amp; Accessibility</Space>,
      children: (
        <Card bordered={false}>
          <Alert
            message="Theme &amp; Visibility"
            description="Choose a display mode that suits your eyesight. High Visibility uses the same colour scheme but with larger text, solid borders and bigger hit-targets. Your selection is saved on this machine."
            type="info"
            style={{ marginBottom: 20 }}
            showIcon
          />
          <Form.Item label="Colour Theme">
            <div className="theme-sample-grid">
              {transportAccentOptions.map((option) => {
                const sample = getTransportPalette(option.value, mode);
                const selected = accent === option.value;
                return (
                  <button
                    type="button"
                    key={option.value}
                    className={`theme-sample-card${selected ? " selected" : ""}`}
                    onClick={() => setAccent(option.value as TransportAccent)}
                    aria-pressed={selected}
                  >
                    <span className="theme-sample-title">{option.label}{selected ? " · Selected" : ""}</span>
                    <span className="theme-sample-preview" style={{ background: sample.appBackground }}>
                      <span className="theme-sample-sidebar" style={{ background: sample.siderBackground }} />
                      <span className="theme-sample-panel" style={{ background: sample.panelBackground, borderColor: sample.primary }}>
                        <span style={{ background: sample.primary }} />
                        <span style={{ background: sample.success }} />
                        <span style={{ background: sample.warning }} />
                        <span style={{ background: sample.error }} />
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Form.Item>

          <Form.Item label="Navigation Layout">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={navMode}
              onChange={(event) => setNavMode(event.target.value as NavMode)}
              options={[
                { value: "sidebar", label: "Sidebar Navigation" },
                { value: "topbar", label: "Top Navigation" },
              ]}
            />
          </Form.Item>

          <Form.Item label="UI Variant">
            <Space direction="vertical" size={12}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={uiVariant}
                onChange={(e) => setUiVariant(e.target.value as UiVariant)}
                options={[
                  { value: "standard",       label: "Standard" },
                  { value: "highVisibility", label: "High Visibility" },
                ]}
              />
              {uiVariant === "highVisibility" && (
                <Tag color="blue" style={{ marginTop: 4 }}>
                  High Visibility active — larger text, bigger controls, full-contrast colours
                </Tag>
              )}
            </Space>
          </Form.Item>

          <Divider />

          <div style={{ fontSize: 13 }}>
            <strong>Entry page shortcuts</strong>
            <table style={{ marginTop: 8, marginBottom: 20, borderCollapse: "collapse", width: "100%" }}>
              <tbody>
                {[
                  ["Alt + N",  "Open new entry form"],
                  ["Ctrl + S", "Save current form"],
                  ["Alt + W",  "Close / cancel current form or drawer"],
                  ["F5",       "Refresh / reload list"],
                  ["F3",       "Focus the search box"],
                ].map(([key, desc]) => (
                  <tr key={key}>
                    <td style={{ padding: "5px 12px 5px 0", whiteSpace: "nowrap" }}>
                      <kbd style={{ padding: "2px 8px", border: "1px solid currentColor", borderRadius: 4, fontFamily: "inherit", fontSize: 12 }}>
                        {key}
                      </kbd>
                    </td>
                    <td style={{ padding: "5px 0" }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <strong>Cross-page navigation shortcuts (work from any page)</strong>
            <table style={{ marginTop: 8, borderCollapse: "collapse", width: "100%" }}>
              <tbody>
                {NAV_SHORTCUTS.map(({ combo, label }) => (
                  <tr key={combo}>
                    <td style={{ padding: "5px 12px 5px 0", whiteSpace: "nowrap" }}>
                      <kbd style={{ padding: "2px 8px", border: "1px solid currentColor", borderRadius: 4, fontFamily: "inherit", fontSize: 12 }}>
                        {combo}
                      </kbd>
                    </td>
                    <td style={{ padding: "5px 0" }}>{label}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ marginTop: 10, opacity: 0.75 }}>
              When the search box returns no results the <strong>New</strong> button pulses to prompt you to create a new record.
            </p>
          </div>
        </Card>
      ),
    },
    {
      key: "about",
      label: <Space><InfoCircleOutlined />About</Space>,
      children: (
        <Card bordered={false}>
          <p>
            <strong>When Accounting is ON:</strong> All consignments, trips, vehicles, and other transport
            transactions are posted to the accounting system automatically.
          </p>
          <p>
            <strong>When Accounting is OFF:</strong> Transport operations work independently without posting to
            accounting. Use this if you are testing the system or if accounting integration is not yet configured.
          </p>
          <p style={{ color: "#ff7a45" }}>
            <WarningOutlined /> Changing the accounting toggle affects all future transactions. Previously posted
            entries remain in the accounting system.
          </p>
          <Divider />
          <p style={{ opacity: 0.6, fontSize: 12 }}>TransportERP · Build UAT2</p>
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginTop: 0 }}>System Settings</h2>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Tabs items={tabItems} defaultActiveKey="accounting" type="card" />
        </Form>
      </Spin>
    </div>
  );
};

export default SettingsPage;
